import type {FC, ReactNode} from 'react';
import {createContext, useContext} from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import type {GridSortModel} from '@mui/x-data-grid';
import {Alert, Button} from '@wellstone-solutions/web';
import type {ContactNote} from 'api/v2';
import {
  PaginatedListGrid,
  RowCount,
  usePaginationParams,
} from 'modules/datagrid';
import {contactNotesQueryOptions} from 'queries';
import {COMPACT_NOTE_LIST_COLUMNS, NOTE_LIST_COLUMNS} from './columns';
import {NoteDetailsProvider, useViewNoteDetails} from './NoteDetailsProvider';

const isSelectingText = () => Boolean(window.getSelection()?.toString());

const DEFAULT_SORT_MODEL: GridSortModel = [
  {field: 'dateOfService', sort: 'desc'},
];

const useNoteListState = () => {
  const pagination = usePaginationParams({
    defaultSortModel: DEFAULT_SORT_MODEL,
  });
  const {page, pageSize, ordering} = pagination;

  const query = useQuery({
    ...contactNotesQueryOptions.list({page, pageSize, ordering}),
    placeholderData: keepPreviousData,
  });

  return {pagination, query};
};

type NoteListContextValue = ReturnType<typeof useNoteListState>;

const NoteListContext = createContext<NoteListContextValue | null>(null);

const useNoteList = (): NoteListContextValue => {
  const context = useContext(NoteListContext);
  if (!context) {
    throw new Error('NoteList parts must be rendered inside <NoteList>');
  }
  return context;
};

type RootProps = {
  children: ReactNode;
};

const Root: FC<RootProps> = ({children}) => {
  const value = useNoteListState();

  return (
    <NoteListContext.Provider value={value}>
      <NoteDetailsProvider>{children}</NoteDetailsProvider>
    </NoteListContext.Provider>
  );
};

const LoadError: FC = () => {
  const {query} = useNoteList();

  if (!query.isError) {
    return null;
  }

  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={() => query.refetch()}>
          Try again
        </Button>
      }>
      Couldn't load notes.
    </Alert>
  );
};

const Count: FC = () => {
  const {query} = useNoteList();

  if (query.isError) {
    return null;
  }

  return (
    <RowCount
      isLoading={query.isFetching}
      label="Note"
      count={query.data?.count ?? 0}
    />
  );
};

type GridProps = {
  compact?: boolean;
};

const Grid: FC<GridProps> = ({compact = false}) => {
  const {query, pagination} = useNoteList();
  const viewDetails = useViewNoteDetails();

  if (query.isError) {
    return null;
  }

  return (
    <PaginatedListGrid
      testId="note-list-datagrid"
      columns={compact ? COMPACT_NOTE_LIST_COLUMNS : NOTE_LIST_COLUMNS}
      rows={query.data?.results ?? []}
      rowCount={query.data?.count ?? 0}
      isLoading={query.isFetching}
      noRowsText="No notes"
      page={pagination.page}
      pageSize={pagination.pageSize}
      ordering={pagination.ordering}
      isMobile={compact}
      onPageChange={pagination.setPage}
      onPageSizeChange={pagination.setPageSize}
      onSortModelChange={pagination.setOrdering}
      onRowClick={({row}: {row: ContactNote}) => {
        if (!isSelectingText()) {
          viewDetails(row);
        }
      }}
    />
  );
};

export const NoteList = Object.assign(Root, {LoadError, Count, Grid});
